// « Lecture de TerObs » (lot Lectures, plan du 12/09 : A3, B1, B3 — issue #107).
//
// UN composant pour tous les blocs de lecture rédigée (sections, analyse d'ensemble,
// synthèse d'accueil) :
//   · le RENDU d'un markdown minimal — paragraphes séparés d'une ligne vide, listes à
//     puces (les pistes), gras **…** — construit en nœuds React, JAMAIS en HTML injecté :
//     une balise écrite dans le texte s'affiche telle quelle ;
//   · l'ATTENTE : quand une lecture est « en préparation », le sondage (`useLecturesEnAttente`,
//     toutes les 5 s, 10 min au plus) la fait apparaître sans rafraîchir, et un compte à
//     rebours « environ N s » suit la durée médiane mesurée par le backend (repli 60 s).
//
// Le sondage lit `GET /observatoire/{insee}/lectures/{cible}` — lecture seule côté serveur,
// même gate que l'écran (403 pour un compte non éligible → on cesse de sonder, sans bruit) :
// il ne déclenche JAMAIS une génération, et jamais un coût pour l'anonyme.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import axios from 'axios'
import { Link, useInRouterContext, useOutletContext, useParams } from 'react-router-dom'
import { ChevronDown, Clock } from 'lucide-react'
import { NOM_PRODUIT } from '../constants'
import { API, type ThemeMeta } from './types'
import { SECTIONS_REMPLACEES, themeDeSection } from './arborescence'
import {
  ancrerAttente, type Generation, generationBloquee, horlogeAttente, libelleAttente,
  messageGeneration, reinitialiserAttente,
} from './attente'
import './lecture.css'

// Branché sur `NOM_PRODUIT` depuis la bascule de marque du 13/09. La valeur ne change
// pas — c'était déjà « Lecture de TerObs », écrit en dur parce que la constante disait
// encore « LObsTer » et aurait rendu l'inverse de la décision A3. Elle dit « TerObs »
// maintenant, donc le libellé repasse par la source de vérité.
export const LIBELLE_LECTURE = `Lecture de ${NOM_PRODUIT}`

export interface LectureState {
  texte?: string; gated?: boolean; status?: string; reason?: 'role'
  created_at?: string; donnees_changees?: boolean
}

// ── Rendu ─────────────────────────────────────────────────────────────────────────────
const _GRAS = /\*\*([^*\n]+?)\*\*/g
// Un paragraphe ouvert par un « Titre court. » (sans chiffre — jamais de faux positif sur
// une phrase qui commence par une donnée), consigne du prompt de synthèse.
const _TITRE = /^([A-ZÀ-ÖØ-Þ][^.:\n0-9]{2,60}\.)\s+/
// Lot B (CDC retours du 08/10 au soir, #623) — la même forme, seule sur sa ligne : « TITRE
// COURT en début de ligne suivi d'un point » (consigne de synthèse, `llm.py`), le paragraphe
// continuant à la ligne suivante.
const _TITRE_LIGNE = /^[A-ZÀ-ÖØ-Þ][^.:\n0-9]{2,60}\.$/
// Une lecture « à parties » compte au moins ce nombre de blocs avant « Pistes : ». La synthèse
// en a cinq au moins (chapô, trois à cinq parties titrées, enjeux) ; une section, deux ou
// trois (`llm.py` : « 2 à 3 paragraphes courts », « aucun titre »).
const BLOCS_LECTURE_A_PARTIES = 4
// #649 — seuil de longueur (caractères d'analyse, avant « Pistes : ») au-delà duquel la lecture
// coule sur trois colonnes. Mesuré sur Besançon (v3.8, 87 lectures) : analyses d'ensemble de
// 1 391 à 2 422 caractères, lectures de section de 605 à 1 860, synthèse 5 141 — 1 400 sépare
// au mieux les ensembles (trois colonnes) des sections (deux) ; une section longue passe à trois.
export const SEUIL_TROIS_COLONNES = 1400
const _PISTES_LIGNE = /^Pistes?\s*:\s*$/
const _PISTES_INLINE = /(Pistes?\s*:)/g
const _PUCE = /^\s*[-•–]\s+/
const _RENVOI = /(→\s.*)$/
// #605 — dans un renvoi, « section <identifiant> » suivi, s'il est là, du titre « … » recopié
// par le prompt (`llm.py` : « → section <identifiant> « <titre exact> » »).
const _SECTION_CITEE = /section\s+([a-z]{2,6}-[a-z0-9]+(?:-[a-z0-9]+)*)(?:\s+«[^»\n]*»)?/g

/** Le chemin vers une section citée, ou `null` : pas de lien. */
export type LienSection = (sectionId: string) => string | null

/** #605 — le résolveur des renvois de la page courante, ou `null` quand le texte doit rester
 *  du texte : hors routeur (PDF, tests), hors d'une commune, sans sommaire servi.
 *  La thématique d'une section se lit dans le registre servi par `/meta` (`themeDeSection`),
 *  JAMAIS dans le préfixe de l'identifiant (A20 R2 : sous v2, log-14 vit dans urb) ; une
 *  section retirée (R7) mène à celle qui la reprend, comme une ancre partagée. */
function useLienSection(): LienSection | null {
  // Les trois crochets lisent un contexte sans exiger de routeur : appelés sans condition.
  const dansRouteur = useInRouterContext()
  const { insee } = useParams<{ insee?: string }>()
  const contexte = useOutletContext<{ meta?: { themes?: ThemeMeta[] } } | null | undefined>()
  const themes = contexte?.meta?.themes
  return useMemo(() => {
    if (!dansRouteur || !insee || !/^[0-9][0-9AB][0-9]{3}$/.test(insee) || !Array.isArray(themes)) {
      return null
    }
    return (id: string) => {
      if (id.endsWith('-ensemble')) return null      // l'analyse d'ensemble n'est pas une section
      let section = id
      let theme = themeDeSection(themes, section)
      if (!theme && SECTIONS_REMPLACEES[id]) {
        section = SECTIONS_REMPLACEES[id]
        theme = themeDeSection(themes, section)
      }
      return theme ? `/o/${insee}/t/${theme}#${section}` : null
    }
  }, [dansRouteur, insee, themes])
}

/** Le renvoi « → … » : chaque « section <id> « titre » » que le registre connaît devient un
 *  lien ; le reste, et tout le renvoi sans résolveur, reste le texte d'aujourd'hui. */
function rendreRenvoi(renvoi: string, cle: string, lien: LienSection | null | undefined): ReactNode {
  if (!lien) return renvoi
  const out: ReactNode[] = []
  let depuis = 0
  for (const m of renvoi.matchAll(_SECTION_CITEE)) {
    const href = m.index === undefined ? null : lien(m[1])
    if (!href || m.index === undefined) continue
    out.push(renvoi.slice(depuis, m.index))
    out.push(<Link key={`${cle}-s${out.length}`} to={href} className="obs2-lecture-lien">{m[0]}</Link>)
    depuis = m.index + m[0].length
  }
  if (out.length === 0) return renvoi
  out.push(renvoi.slice(depuis))
  return out
}

/** Gras **…**, marqueur « Piste(s) : » et renvoi « → … » — en nœuds React. Avec `lien`, les
 *  sections citées dans un renvoi deviennent des liens (#605). */
export function rendreInline(texte: string, cle: string, lien?: LienSection | null): ReactNode[] {
  const out: ReactNode[] = []
  let i = 0
  texte.split(_GRAS).forEach((part, k) => {
    // split avec groupe capturant : les indices impairs sont l'intérieur des **…**
    if (k % 2 === 1) { out.push(<strong key={`${cle}-g${i++}`}>{part}</strong>); return }
    part.split(_PISTES_INLINE).forEach(morceau => {
      if (/^Pistes?\s*:$/.test(morceau)) {
        out.push(<strong key={`${cle}-p${i++}`}>{morceau}</strong>)
        return
      }
      const m = morceau.match(_RENVOI)
      if (m && m.index !== undefined) {
        out.push(morceau.slice(0, m.index))
        out.push(
          <span key={`${cle}-r${i}`} className="obs2-lecture-renvoi">
            {rendreRenvoi(m[1], `${cle}-r${i}`, lien)}
          </span>)
        i++
      } else if (morceau) {
        out.push(morceau)
      }
    })
  })
  return out
}

// Virgule décimale française (08/10, relecture de Cyrille sur Besançon : « 2.6 années »,
// « 43.72 % » dans des lectures servies). La consigne la demande depuis v3.7 ; ceci corrige
// les textes déjà en cache, sans régénération. Seul un nombre DÉCIMAL isolé est touché :
// ni une version (« v3.4 », précédée d'une lettre), ni une date pointée (« 08.10.2026 »),
// ni un identifiant de section (« rev-1 », sans point).
const _DECIMAL_ANGLAIS = /(?<![\p{L}\d.,])(\d+)\.(\d+)(?![\p{L}\d])(?!\.\d)/gu
export function virguleDecimale(texte: string): string {
  return texte.replace(_DECIMAL_ANGLAIS, '$1,$2')
}

/** Le texte d'une lecture en paragraphes, listes et gras.
 *
 *  #564 (« comme un journal, en rubriques »), refait pour #649 (Cyrille, 09/10) : tout le
 *  texte d'analyse — ce qui précède la première ligne « Pistes : », chapô compris — coule dans
 *  UN bloc `.obs2-lecture-colonnes` à nombre de colonnes fixe : trois quand le texte est long
 *  (Vue d'ensemble, analyse d'ensemble d'une thématique), deux sinon (lecture de section). Le
 *  nombre se décide sur la LONGUEUR du texte (`SEUIL_TROIS_COLONNES`), jamais sur le nombre de
 *  paragraphes, et jamais une seule colonne sur grand écran : plus d'alternance une colonne /
 *  deux colonnes / une colonne. Le chapô garde sa mise en valeur en tête de la première
 *  colonne ; les paragraphes se coupent entre deux colonnes (règles dans `lecture.css`) ; en
 *  dessous de 920 px deux colonnes, de 700 px une, une à l'impression et dans le PDF. Les
 *  pistes suivent, hors colonnes, sur toute la largeur. */
export function LectureTexte({ texte }: { texte: string }) {
  const lien = useLienSection()
  const blocs = virguleDecimale(texte).replace(/\r\n/g, '\n').split(/\n{2,}/)
  // Lot B (08/10) — le faux titre. « Le parc est ancien. Près de 40 %… » en tête du second
  // paragraphe d'une section était pris pour un titre : il ouvrait une rubrique qui avalait le
  // paragraphe suivant, et la lecture perdait une colonne. La forme en ligne « Titre court.
  // Texte… » n'est donc un titre que dans une lecture à parties ; ailleurs, seul un titre posé
  // sur sa propre ligne en est un.
  const aParties = blocsAvantPistes(blocs) >= BLOCS_LECTURE_A_PARTIES
  // L'analyse en RUBRIQUES (revue codex P2) : un paragraphe titré ouvre une rubrique qui garde
  // ses paragraphes et listes sans titre qui suivent — la consigne de synthèse permet une
  // rubrique en deux paragraphes, qu'aucune colonne ne doit séparer. Hors titre, chaque
  // paragraphe est sa propre rubrique.
  const rubriques: ReactNode[][] = []
  const pistes: ReactNode[] = []
  let dansPistes = false
  let rubriqueTitree = false
  let n = 0                     // compteur de clés, commun aux deux parties
  const pousser = (noeud: ReactNode, titre = false) => {
    if (dansPistes) { pistes.push(noeud); return }
    if (titre || !rubriqueTitree || rubriques.length === 0) {
      rubriques.push([noeud]); rubriqueTitree = titre
    } else {
      rubriques[rubriques.length - 1].push(noeud)
    }
  }
  blocs.forEach((bloc, b) => {
    const lignes = bloc.split('\n').map(l => l.trimEnd()).filter(l => l.trim() !== '')
    let para: string[] = []
    let puces: string[] = []
    const videParagraphe = () => {
      if (para.length === 0) return
      const lignesPara = para
      const p = para.join(' ')
      para = []
      const cle = `b${b}-p${n++}`
      // Le chapô : premier paragraphe du premier bloc, en tête de l'analyse.
      const estChapo = b === 0 && !dansPistes && rubriques.length === 0
      // Codex P2 (LlmTexte) : jamais de titre sur le 1er bloc — c'est le chapô.
      const titreLigne = b > 0 && lignesPara.length > 1 && _TITRE_LIGNE.test(lignesPara[0])
      const m = b > 0 && (aParties || titreLigne) ? p.match(_TITRE) : null
      if (m) {
        pousser(
          <p key={cle}>
            <strong className="obs2-lecture-titre">{m[1]}</strong>
            {rendreInline(p.slice(m[0].length), cle, lien)}
          </p>, true)
      } else {
        pousser(
          <p key={cle} className={estChapo ? 'obs2-lecture-chapo' : undefined}>
            {rendreInline(p, cle, lien)}
          </p>)
      }
    }
    const videListe = () => {
      if (puces.length === 0) return
      const cle = `b${b}-l${n++}`
      pousser(
        <ul key={cle}>
          {puces.map((pu, j) => <li key={j}>{rendreInline(pu, `${cle}-${j}`, lien)}</li>)}
        </ul>)
      puces = []
    }
    lignes.forEach(l => {
      if (_PISTES_LIGNE.test(l.trim())) {
        videParagraphe(); videListe()
        dansPistes = true
        pousser(<p key={`b${b}-t${n++}`} className="obs2-lecture-pistes">{l.trim()}</p>)
      } else if (_PUCE.test(l)) {
        videParagraphe()
        puces.push(l.replace(_PUCE, ''))
      } else {
        videListe()
        para.push(l.trim())
      }
    })
    videParagraphe(); videListe()
  })
  // #649 (Cyrille, 09/10) : un seul bloc en colonnes pour tout le texte d'analyse, chapô
  // compris — trois colonnes quand le texte est long, deux sinon ; décidé sur la LONGUEUR du
  // texte, jamais sur le nombre de paragraphes, jamais une seule colonne sur grand écran.
  const nbColonnes = longueurAvantPistes(blocs) >= SEUIL_TROIS_COLONNES ? 3 : 2
  const classeColonnes = `obs2-lecture-colonnes obs2-lecture-colonnes-${nbColonnes}`
  return (
    <div className="obs2-lecture">
      {rubriques.length > 0 && (
        <div className={classeColonnes}>
          {rubriques.map((r, k) => r.length === 1 ? r[0]
            : <div key={`r${k}`} className="obs2-lecture-rubrique">{r}</div>)}
        </div>)}
      {pistes}
    </div>
  )
}

/** #649 — la longueur, en caractères, du texte d'analyse avant la ligne « Pistes : » (les
 *  lignes d'un bloc jointes par une espace ; les pistes ne comptent pas). */
export function longueurAvantPistes(blocs: string[]): number {
  let n = 0
  for (const bloc of blocs) {
    const lignes = bloc.split('\n').map(l => l.trim()).filter(l => l !== '')
    const i = lignes.findIndex(l => _PISTES_LIGNE.test(l))
    if (i === -1) { n += lignes.join(' ').length; continue }
    n += lignes.slice(0, i).join(' ').length
    break
  }
  return n
}

/** Lot B (08/10) — le nombre de blocs d'analyse (séparés d'une ligne vide) avant la ligne
 *  « Pistes : » : ce qui distingue une lecture à parties (synthèse) d'une section. */
function blocsAvantPistes(blocs: string[]): number {
  let n = 0
  for (const bloc of blocs) {
    const lignes = bloc.split('\n').map(l => l.trim()).filter(l => l !== '')
    const i = lignes.findIndex(l => _PISTES_LIGNE.test(l))
    if (i === -1) { if (lignes.length > 0) n++; continue }
    if (i > 0) n++          // l'analyse qui précède « Pistes : » dans le même bloc
    break
  }
  return n
}

// ── Attente ───────────────────────────────────────────────────────────────────────────
/** « environ N s » : décompte à partir de la médiane servie, sans jamais remonter quand un
    nouveau sondage rapporte la même médiane ; à zéro, on ne ment pas avec un chiffre. */
/** Le décompte d'attente d'une lecture. Toute la règle — l'horloge de page, le libellé,
 *  les motifs de blocage — vit dans `obs/attente.ts` : ici, rien que l'affichage. */
export function CompteARebours({ attenteS, generation }: {
  attenteS: number | null; generation?: Generation | null
}) {
  const [ecouleS, setEcouleS] = useState(0)
  // Repli quand l'horloge de page n'est pas armée (usage isolé, tests) : on ancre au
  // montage. Jamais l'inverse — l'horloge de page prime toujours.
  // L'ancre et la lecture de l'heure vivent dans l'EFFET, jamais dans le rendu : un
  // `Date.now()` appelé pendant le rendu est une fonction impure, et React n'en garantit
  // pas le nombre d'appels.
  const montage = useRef<number | null>(null)
  useEffect(() => {
    if (montage.current === null) montage.current = Date.now()
    const id = setInterval(() => {
      setEcouleS((Date.now() - (horlogeAttente().debut || montage.current!)) / 1000)
    }, 500)
    return () => clearInterval(id)
  }, [])

  const horloge = horlogeAttente()
  const etat = generation ?? horloge.generation
  if (generationBloquee(etat)) {
    // Plus de compte à rebours sur une rédaction qui n'aura pas lieu : le motif, et ce
    // qui reste vrai de la page.
    return (
      <div className="obs2-lecture-attente" role="status" aria-live="polite">
        <Clock size={15} aria-hidden="true" />
        <span>{messageGeneration(etat)}</span>
      </div>
    )
  }
  const { texte, fort } = libelleAttente(horloge.annonce ?? attenteS, ecouleS)
  return (
    <div className="obs2-lecture-attente" role="status" aria-live="polite">
      <Clock size={15} aria-hidden="true" />
      <span>{texte}{fort ? <> <strong>{fort}</strong></> : null}</span>
    </div>
  )
}

export interface Pretes {
  attente_s: number; pretes: Record<string, string>; generation?: Generation
  details?: Record<string, LectureState>
}

const INTERVALLE_MS = 5000
const DUREE_MAX_MS = 10 * 60 * 1000

/** Sonde les lectures attendues (`idsEnAttente`) d'une cible (thématique ou « synthese »)
    toutes les 5 s, 10 min au plus, tant qu'il en manque. Rend les textes prêts (fusionnés
    par l'appelant) et la médiane d'attente. Un 403/404 arrête le sondage : ce compte ne
    verra pas de lecture arriver, inutile d'insister.
    `empreinte` (14/10) : l'empreinte des données de la page (`empreinte_lectures`) ; le
    serveur signale les lectures antérieures aux données, sans les masquer (Marc, 21/10). */
export function useLecturesEnAttente(insee: string | undefined, cible: string | undefined,
                                     idsEnAttente: string[], token: string | null,
                                     generationInitiale?: Generation | null,
                                     empreinte?: string | null) {
  const [pretes, setPretes] = useState<Record<string, string>>({})
  const [details, setDetails] = useState<Record<string, LectureState>>({})
  const [attenteS, setAttenteS] = useState<number | null>(null)
  const [generation, setGeneration] = useState<Generation | null>(null)
  const pretesRef = useRef(pretes)
  pretesRef.current = pretes
  // Nouvelle commune ou nouvelle cible : on repart de zéro (jamais le texte d'une autre
  // page, jamais l'horloge d'attente de la précédente).
  useEffect(() => {
    setPretes({}); setDetails({}); setAttenteS(null); setGeneration(null); reinitialiserAttente()
  }, [insee, cible, empreinte])
  const restants = idsEnAttente.filter(id => !pretes[id])
  const actif = restants.length > 0 && !!insee && !!cible && !!token
  // L'état servi AVEC la thématique arme l'horloge dès le premier rendu : sans lui, un
  // abonné arrivant sur une commune dont la génération est coupée voyait un décompte
  // pendant les 5 s du premier sondage. Le sondage confirmera ou corrigera ensuite.
  useEffect(() => {
    if (actif && insee && cible) ancrerAttente(`${insee}/${cible}`, null, generationInitiale ?? null)
  }, [actif, insee, cible, generationInitiale])
  const cleIds = idsEnAttente.join(',')
  // Hors démo, essai ou abonnement (14/10) : aucune lecture ne sera jamais rédigée pour ce
  // territoire — on ne sonde pas pour rien (revue codex P2). L'horloge, elle, est ancrée plus
  // haut avec cet état, et l'écran affiche le motif.
  // Gel de l'API (22/10) : rien ne sera rédigé, on ne sonde pas non plus.
  const horsEspace = generationInitiale?.etat === 'hors_espace' || generationInitiale?.etat === 'api_gelee'
  useEffect(() => {
    if (!actif || horsEspace) return
    let alive = true
    let id: ReturnType<typeof setInterval> | null = null
    const debut = Date.now()
    const stop = () => { if (id !== null) { clearInterval(id); id = null } }
    const tick = async () => {
      try {
        const r = await axios.get<Pretes>(`${API}/observatoire/${insee}/lectures/${cible}`,
          { timeout: 8000, headers: { Authorization: `Bearer ${token}` },
            ...(empreinte ? { params: { empreinte } } : {}) })
        if (!alive) return
        setAttenteS(r.data.attente_s ?? null)
        setGeneration(r.data.generation ?? null)
        if (r.data.generation?.etat === 'hors_espace' || r.data.generation?.etat === 'api_gelee') stop()
        // L'horloge de page est armée au PREMIER sondage et jamais réarmée : les
        // sondages suivants n'apportent que l'état de génération, plus une promesse.
        ancrerAttente(`${insee}/${cible}`, r.data.attente_s ?? null,
                      r.data.generation ?? null)
        const recues = r.data.pretes || {}
        if (Object.keys(recues).length > 0) setPretes(prev => ({ ...prev, ...recues }))
        if (r.data.details) setDetails(prev => ({ ...prev, ...r.data.details }))
      } catch (err) {
        if (!alive) return
        const code = (err as { response?: { status?: number } })?.response?.status
        if (code === 403 || code === 404) stop()   // pas éligible : on n'insiste pas
      }
      if (Date.now() - debut >= DUREE_MAX_MS) stop()
    }
    void tick()
    id = setInterval(() => { void tick() }, INTERVALLE_MS)
    return () => { alive = false; stop() }
    // `cleIds` remplace le tableau (nouvelle identité à chaque rendu) ; `actif` porte token/insee/cible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif, horsEspace, cleIds, insee, cible, token, empreinte])
  return { pretes, details, attenteS, generation }
}

/** La date vient de la rédaction, jamais de l'heure du navigateur. */
export function DateLecture({ c }: { c: LectureState }) {
  if (!c.donnees_changees || !c.created_at) return null
  const date = new Date(c.created_at)
  if (Number.isNaN(date.getTime())) return null
  const jour = date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
  return <p className="obs2-lecture-date">Lecture rédigée le {jour}. Les données ont été mises à jour depuis.</p>
}

// ── Le bloc ───────────────────────────────────────────────────────────────────────────
/** Repliable (comme LlmBlock) : l'en-tête bascule, ouvert par défaut, état local. */
export function LectureTerobs({ c, label = LIBELLE_LECTURE, attenteS = null }: {
  c: LectureState; label?: string; attenteS?: number | null
}) {
  const [ouvert, setOuvert] = useState(true)
  return (
    <div className="obs2-llm">
      <button type="button" className="obs2-llm-head" style={{ width: '100%', border: 'none',
          background: 'transparent', cursor: 'pointer', padding: 0, textAlign: 'left' }}
        aria-expanded={ouvert}
        aria-label={label ? undefined : `${LIBELLE_LECTURE} — déplier ou replier`}
        onClick={() => setOuvert(o => !o)}>
        <div className="obs2-llm-icon" aria-hidden="true">L</div>
        {label ? <div className="obs2-llm-label">{label}</div> : null}
        <ChevronDown size={15} aria-hidden="true"
          style={{ marginLeft: 'auto', transition: 'transform .15s',
                   transform: ouvert ? 'none' : 'rotate(-90deg)' }} />
      </button>
      {ouvert && (c.texte
        ? <><LectureTexte texte={c.texte} /><DateLecture c={c} /></>
        : <CompteARebours attenteS={attenteS} />)}
    </div>
  )
}
